import {
  Cartesian3,
  CallbackProperty,
  JulianDate,
  Color,
  LagrangePolynomialApproximation,
  LinearApproximation,
} from "cesium";
import { CinemaCamera } from "./cinemaCamera";
import { isFlightHidden } from "./flightVisibility";

/**
 * Replay-smoothing controller (one lazily-created instance per viewer).
 *
 * The app runs in "smoothcam" by default: glassy flight motion + a calm camera
 * that no longer wobbles through thermals, while keeping every existing control
 * and full mouse orbit/zoom. "classic" and "motion" remain implemented for a
 * possible future in-app toggle (there is currently no UI that switches modes).
 *
 * Modes:
 *  - classic   : original — linear interpolation, camera follows raw position.
 *  - motion    : glassy motion — Lagrange interpolation of the marker path.
 *                Camera unchanged (classic trackedEntity, orbits thermals).
 *  - smoothcam : motion + a calm camera. The camera still uses Cesium's
 *                trackedEntity (so mouse orbit/zoom keeps working), but it
 *                tracks an invisible PROXY that sits on the thermal core
 *                (centred moving-average of the pilot), instead of the raw
 *                circling position. windowSec tunes how much it calms.
 */

const PROXY_ID = "__replayProxy";

// Default chase-camera offset from the followed point, in its local
// East-North-Up frame (metres): (east, north, up). Sets the STARTING view angle;
// the user can orbit/zoom freely afterwards. All three ~equal → a ~35° oblique
// "side" view from ~4 km, instead of the old near-top-down (4000 up vs ~1400
// side ≈ 70°). Lower the up value for a flatter/more side-on view. Tune here.
export const CHASE_OFFSET = new Cartesian3(2300, -2300, 2300);

// Cap how far the smoothed look-at may trail the real (thermal-core) target.
// Small movements (thermal circles) ease gently below this; big jumps — timeline
// scrubbing, especially the large drag deltas on mobile, and high-speed
// playback — snap to within this distance so the pilot never leaves the frame.
const MAX_LAG_M = 250;

class ReplayModes {
  constructor(viewer) {
    this.viewer = viewer;
    this.mode = "smoothcam"; // default experience: smooth motion + calm camera
    this.windowSec = 48; // centred averaging window for smoothcam (seconds)
    this.interpDegree = 2; // Lagrange degree for motion smoothing (gentle, no overshoot)
    this.followEntity = null; // the real pilot the proxy smooths
    this.smTarget = null; // smoothed centre (world Cartesian3) reported by the proxy
    this.proxy = null;
    this.lastMs = 0;
    this._started = false;
    this._listeners = new Set();
    // Optional "cinema" layer: picks the camera's angle + altitude from the
    // shape of the track. Off by default; toggled per pilot from the toolbar.
    this.cinema = new CinemaCamera(this);
  }

  onChange(cb) {
    this._listeners.add(cb);
    return () => this._listeners.delete(cb);
  }
  _emit() {
    const s = {
      mode: this.mode,
      windowSec: this.windowSec,
      followedId: this.followedId,
      cinema: this.cinemaOn,
    };
    this._listeners.forEach((cb) => cb(s));
  }

  get cinemaOn() {
    return this.cinema.enabled;
  }

  /**
   * Cinema follows whoever is followed, so switching it on for a pilot also
   * follows them. Turning it off leaves the camera exactly where it is —
   * normal smooth follow (and the mouse) simply take over again.
   */
  setCinema(on, entity) {
    this._ensureStarted();
    if (on) {
      if (entity && this._isHidden(entity)) return; // nothing on screen to film
      if (entity) this.followPilot(entity);
      if (!this.followEntity) return; // nothing to follow → nothing to film
      this.cinema.enable();
    } else {
      this.cinema.disable();
    }
    this._emit();
  }

  /**
   * The id of the pilot currently being followed, in EITHER mode. In smoothcam
   * the camera tracks the invisible proxy, so the *logical* target is
   * followEntity; in classic/motion it's whatever trackedEntity points at.
   * The toolbar uses this for its follow/unfollow toggle and row highlight.
   */
  get followedId() {
    if (this.mode === "smoothcam") return this.followEntity ? this.followEntity.id : null;
    const te = this.viewer.trackedEntity;
    return te && te !== this.proxy ? te.id : null;
  }

  /**
   * Follow a pilot the right way for the current mode (proxy in smoothcam).
   * Refuses a flight the user has unchecked — see `_isHidden`.
   */
  followPilot(entity) {
    if (!entity || !entity.position || this._isHidden(entity)) return;
    if (this.mode === "smoothcam") {
      this._ensureProxy();
      this.followEntity = entity;
      this._seed();
      this.viewer.trackedEntity = this.proxy;
    } else {
      this.viewer.trackedEntity = entity; // classic raw follow (viewFrom set by toolbar)
    }
    this._emit();
  }

  /** Stop following in either mode. */
  unfollow() {
    if (this.mode === "smoothcam") this.followEntity = null;
    this.cinema.disable(); // nothing to film once the camera is free
    this.viewer.trackedEntity = undefined;
    this._emit();
  }

  _ensureStarted() {
    if (this._started) return;
    this._started = true;

    this.viewer.clock.onTick.addEventListener(() => this._tick());

    // Re-apply the current interpolation to freshly loaded flights.
    this.viewer.dataSources.dataSourceAdded.addEventListener((collection, ds) => {
      if (this.mode !== "classic") this._applyInterpToDataSource(ds);
    });

    // Keep followedId (and the toolbar highlight) correct in every mode, and in
    // smoothcam convert any follow (auto on load, per-pilot button, name click)
    // into a smoothed proxy-follow so all the existing controls still work.
    this.viewer.trackedEntityChanged.addEventListener((te) => {
      // INVARIANT: the camera never sits on a hidden flight. Not everything
      // goes through followPilot — `zoomToEntityBound` (pilot name click, chart
      // click) and the data-source load listener assign `trackedEntity`
      // directly — but every one of them raises this event, so this is the one
      // place that can enforce it for all of them. Rejecting here re-fires the
      // event with undefined, which the `!te` branch below tidies up.
      if (te && te !== this.proxy && this._isHidden(te)) {
        this.cinema.disable();
        this.viewer.trackedEntity = undefined;
        return;
      }
      if (this.mode === "smoothcam") {
        if (te && te !== this.proxy && te.position) {
          this._ensureProxy(); // proxy must exist before we hand it the camera
          this.followEntity = te;
          this._seed();
          this.viewer.trackedEntity = this.proxy; // re-fires with proxy (ignored below)
          this._emit();
          return;
        }
        if (!te) {
          this.followEntity = null;
          this._emit();
        }
        return; // te === proxy: no logical change
      }
      // classic / motion: the tracked entity IS the followed pilot.
      this._emit();
    });

    // Apply the current (default) mode to anything already loaded/tracked, in
    // case the controller is created after data exists.
    if (this.mode !== "classic") this._applyInterpAll(false);
    if (this.mode === "smoothcam") {
      this._ensureProxy(); // create it up front so auto-follow-on-load can zoom in
      const te = this._currentTrackedReal();
      if (te) this.followPilot(te);
    }
  }

  _ensureProxy() {
    if (this.proxy) return this.proxy;
    // Position-only would give trackedEntity a zero/failed bounding sphere and
    // break tracking — so attach a 1px fully-transparent point. Nothing is
    // visible, but Cesium gets a bounding sphere and follows normally.
    this.proxy = this.viewer.entities.add({
      id: PROXY_ID,
      position: new CallbackProperty(() => this.smTarget, false),
      point: {
        pixelSize: 1,
        color: Color.TRANSPARENT,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
      },
      viewFrom: CHASE_OFFSET, // oblique side view (see constant above)
    });
    return this.proxy;
  }

  setMode(mode, primaryEntity) {
    this._ensureStarted();
    const prev = this.mode;
    this.mode = mode;

    // --- motion: interpolation of the marker path ---
    this._applyInterpAll(mode === "classic");

    // --- camera ---
    if (mode === "smoothcam") {
      const ent =
        primaryEntity && primaryEntity.position
          ? primaryEntity
          : this.followEntity || this._currentTrackedReal();
      if (ent) {
        this.followEntity = ent;
        this._ensureProxy();
        this._seed();
        this.viewer.trackedEntity = this.proxy;
      }
    } else if (prev === "smoothcam") {
      // Leaving smooth camera → hand control back to the real pilot (classic follow).
      if (this.viewer.trackedEntity === this.proxy) {
        this.viewer.trackedEntity = this.followEntity || undefined;
      }
    }
    this._emit();
  }

  setWindow(sec) {
    this.windowSec = sec;
    this._emit();
  }

  /**
   * A pilot entity belongs to a flight the user has unchecked. Entity ids are
   * the flight ids (`czmlHelper` gives the packet `id: id`), in the plain and
   * the `_colored` data source alike, so the visibility store answers directly.
   */
  _isHidden(entity) {
    return !!entity && entity !== this.proxy && isFlightHidden(entity.id);
  }

  _currentTrackedReal() {
    const te = this.viewer.trackedEntity;
    return te && te !== this.proxy && te.position ? te : null;
  }

  _applyInterpAll(linear) {
    const dss = this.viewer.dataSources;
    for (let i = 0; i < dss.length; i++) {
      this._applyInterpToDataSource(dss.get(i), linear);
    }
  }

  _applyInterpToDataSource(ds, linear = this.mode === "classic") {
    for (const e of ds.entities.values) {
      const p = e.position;
      if (p && typeof p.setInterpolationOptions === "function") {
        p.setInterpolationOptions(
          linear
            ? { interpolationAlgorithm: LinearApproximation, interpolationDegree: 1 }
            : {
                interpolationAlgorithm: LagrangePolynomialApproximation,
                interpolationDegree: this.interpDegree,
              }
        );
      }
    }
  }

  _seed() {
    this.smTarget = this._averagedPosition();
    this.lastMs = performance.now();
  }

  /** Centred moving average of the followed pilot over windowSec (≈ thermal core). */
  _averagedPosition() {
    const ent = this.followEntity;
    if (!ent || !ent.position) return null;
    const clock = this.viewer.clock;
    const half = this.windowSec / 2;
    const step = Math.max(1, this.windowSec / 16);
    const { startTime, stopTime, currentTime } = clock;

    const acc = new Cartesian3(0, 0, 0);
    const scratch = new Cartesian3();
    const jd = new JulianDate();
    let n = 0;
    for (let d = -half; d <= half + 1e-6; d += step) {
      let t = JulianDate.addSeconds(currentTime, d, jd);
      if (JulianDate.lessThan(t, startTime)) t = startTime;
      else if (JulianDate.greaterThan(t, stopTime)) t = stopTime;
      const p = ent.position.getValue(t, scratch);
      if (p) {
        Cartesian3.add(acc, p, acc);
        n++;
      }
    }
    if (n > 0) return Cartesian3.multiplyByScalar(acc, 1 / n, new Cartesian3());
    return ent.position.getValue(currentTime, new Cartesian3()) || null;
  }

  _tick() {
    if (this.mode !== "smoothcam" || !this.followEntity) return;
    // Safety net for the same invariant: in smoothcam `trackedEntity` is the
    // proxy, so hiding the *followed* pilot raises no trackedEntityChanged at
    // all. Anything that hides a flight without handing the camera over is
    // caught here on the next tick (one Set lookup).
    if (this._isHidden(this.followEntity)) {
      this.unfollow();
      return;
    }
    const raw = this._averagedPosition();
    if (!raw) return;

    const nowMs = performance.now();
    let dt = (nowMs - this.lastMs) / 1000;
    this.lastMs = nowMs;
    if (!(dt > 0) || dt > 0.25) dt = 1 / 60;

    if (!this.smTarget) {
      this.smTarget = raw;
      this.cinema.update(dt);
      return;
    }
    const a = 1 - Math.exp(-dt / 0.4); // gentle residual easing
    Cartesian3.lerp(this.smTarget, raw, a, this.smTarget);

    // Bound the lag: on a fast scrub/jump the eased target would trail far
    // behind and push the pilot off-screen — clamp it back near the real one.
    const gap = Cartesian3.distance(this.smTarget, raw);
    if (gap > MAX_LAG_M) {
      const dir = Cartesian3.subtract(this.smTarget, raw, new Cartesian3());
      Cartesian3.normalize(dir, dir);
      Cartesian3.multiplyByScalar(dir, MAX_LAG_M, dir);
      Cartesian3.add(raw, dir, this.smTarget);
    }

    // Last: the target is final for this frame, so cinema can place the camera
    // on it. Cesium's EntityView already ran (clock.onTick), so we win the frame.
    this.cinema.update(dt);
  }
}

export function getReplayModes(viewer) {
  if (!viewer.__replayModes) {
    viewer.__replayModes = new ReplayModes(viewer);
    viewer.__replayModes._ensureStarted(); // listeners live from the start (classic = no-op)
  }
  return viewer.__replayModes;
}
